import React, { useContext, useState, useEffect } from "react";
import { AuthContext } from "../context/authContext";
import { useQuery, useMutation } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { useToast } from "../context/ToastContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Loader2, User, Settings, MapPin, Mail, Phone, Lock, Save, Briefcase } from "lucide-react";

const AdminSettingsPanel = ({ open, onOpenChange }) => {
    const { user, dispatch } = useContext(AuthContext);
    const { addToast } = useToast();

    // -- Profile State --
    const [profileData, setProfileData] = useState({
        name: '',
        phone_number: ''
    });

    // -- Warehouse State --
    const [warehouseData, setWarehouseData] = useState({
        name: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        contact_number: '',
        manager_name: ''
    });

    // Load initial data
    useEffect(() => {
        if (user) {
            setProfileData({
                name: user.name || '',
                phone_number: user.phone_number || ''
            });
        }
    }, [user]);

    const { data: warehouse } = useQuery({
        queryKey: ['warehouse'],
        queryFn: async () => {
            const { data } = await apiClient.get('/admin/warehouse');
            return data || {};
        },
        enabled: open,
    });

    useEffect(() => {
        if (warehouse) {
            setWarehouseData({
                name: warehouse.name || '',
                address: warehouse.address || '',
                city: warehouse.city || '',
                state: warehouse.state || '',
                zipCode: warehouse.zipCode || '',
                contact_number: warehouse.contact_number || '',
                manager_name: warehouse.manager_name || ''
            });
        }
    }, [warehouse]);

    // -- Mutations --

    // 1. Update Profile
    const profileMutation = useMutation({
        mutationFn: async (data) => {
            const { data: updatedUser } = await apiClient.put('/users/me', data);
            return updatedUser;
        },
        onSuccess: (updatedUser) => {
            addToast("Profile updated successfully!", "success");
            // Force reload to sync all user data across the app
            window.location.reload();
        },
        onError: (err) => {
            addToast(err.response?.data?.msg || "Failed to update profile", "error");
        }
    });

    // 2. Update Warehouse
    const warehouseMutation = useMutation({
        mutationFn: async (data) => {
            return await apiClient.post('/admin/warehouse', data);
        },
        onSuccess: () => {
            addToast("Warehouse operational details saved.", "success");
            onOpenChange(false);
        },
        onError: () => {
            addToast("Failed to update warehouse.", "error");
        }
    });

    // -- Handlers --
    const handleProfileChange = (e) => {
        setProfileData({ ...profileData, [e.target.name]: e.target.value });
    };

    const handleWarehouseChange = (e) => {
        setWarehouseData({ ...warehouseData, [e.target.name]: e.target.value });
    };

    const handleProfileSubmit = () => {
        profileMutation.mutate(profileData);
    };

    const handleWarehouseSubmit = () => {
        warehouseMutation.mutate(warehouseData);
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            {/* Added 'overflow-hidden' to main content to manage internal scroll properly */}
            <SheetContent className="w-full sm:max-w-md md:max-w-lg border-l border-zinc-200 dark:border-zinc-800 shadow-2xl p-0 bg-white dark:bg-zinc-950 flex flex-col h-full">

                <Tabs defaultValue="profile" className="flex flex-col h-full w-full">
                    {/* Header + Tabs List Section (Sticky) */}
                    <div className="flex-none z-20 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-200 dark:border-zinc-800 p-6 pb-4">
                        <SheetHeader className="flex flex-row items-center justify-between mb-6">
                            <div className="flex flex-col gap-1">
                                <SheetTitle className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50 flex items-center gap-2">
                                    Settings
                                </SheetTitle>
                                <p className="text-xs text-zinc-500 dark:text-zinc-400 font-medium">Manage your account and preferences</p>
                            </div>
                        </SheetHeader>

                        <TabsList className="grid w-full grid-cols-2 p-1 bg-zinc-100/80 dark:bg-zinc-900/80 rounded-xl border border-zinc-200/50 dark:border-zinc-800/50">
                            <TabsTrigger value="profile" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-800 data-[state=active]:shadow-sm data-[state=active]:text-zinc-900 dark:data-[state=active]:text-zinc-100 text-zinc-500 transition-all duration-300 font-medium text-sm py-2">Profile</TabsTrigger>
                            <TabsTrigger value="warehouse" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-800 data-[state=active]:shadow-sm data-[state=active]:text-zinc-900 dark:data-[state=active]:text-zinc-100 text-zinc-500 transition-all duration-300 font-medium text-sm py-2">Warehouse</TabsTrigger>
                        </TabsList>
                    </div>

                    {/* Scrollable Content Area - Hidden Scrollbar */}
                    <div className="flex-1 overflow-y-auto no-scrollbar scroll-smooth">
                        <style>{`.no-scrollbar::-webkit-scrollbar { display: none; } .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}</style>

                        {/* --- PROFILE TAB --- */}
                        <TabsContent value="profile" className="m-0 p-0 focus-visible:outline-none animate-in fade-in slide-in-from-bottom-4 duration-500 ease-out">
                            <div className="p-6 space-y-6 pb-24">

                                {/* Hero Card */}
                                <div className="group relative overflow-hidden rounded-2xl bg-zinc-900 dark:bg-zinc-50 p-6 text-white dark:text-zinc-900 shadow-xl ring-1 ring-black/5 transition-all duration-300 hover:shadow-2xl hover:scale-[1.01]">
                                    {/* Abstract Gradient Background */}
                                    <div className="absolute top-0 right-0 -mt-12 -mr-12 w-48 h-48 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-full blur-[60px] opacity-60 dark:opacity-40" />
                                    <div className="absolute bottom-0 left-0 -mb-12 -ml-12 w-40 h-40 bg-gradient-to-tr from-blue-500 to-cyan-500 rounded-full blur-[50px] opacity-40 dark:opacity-30" />

                                    <div className="relative z-10 flex items-center gap-5">
                                        <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/20 flex items-center justify-center text-white dark:text-zinc-900 backdrop-blur-md shadow-inner group-hover:rotate-3 transition-transform duration-500">
                                            <User className="h-9 w-9 text-white dark:text-zinc-900 drop-shadow-md" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-zinc-400 dark:text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1">Current User</p>
                                            <h3 className="font-bold text-2xl tracking-tight text-white dark:text-zinc-900 mb-2">{user?.name}</h3>
                                            <Badge className="bg-white/10 hover:bg-white/20 dark:bg-black/10 dark:hover:bg-black/20 text-white dark:text-zinc-900 border-0 backdrop-blur-md px-3 py-1 text-xs">
                                                {user?.role}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>

                                {/* Edit Form */}
                                <div className="space-y-6">
                                    <div className="flex items-center gap-3 pb-3 border-b border-zinc-100 dark:border-zinc-800">
                                        <div className="bg-zinc-100 dark:bg-zinc-800 p-1.5 rounded-md">
                                            <User className="w-4 h-4 text-zinc-600 dark:text-zinc-400" />
                                        </div>
                                        <h4 className="font-semibold text-sm text-zinc-900 dark:text-zinc-100">Personal Details</h4>
                                    </div>

                                    <div className="grid gap-5">
                                        <div className="grid gap-2 group">
                                            <Label htmlFor="profileName" className="text-xs text-zinc-500 font-medium group-focus-within:text-violet-600 transition-colors">Full Name</Label>
                                            <Input
                                                id="profileName"
                                                name="name"
                                                value={profileData.name}
                                                onChange={handleProfileChange}
                                                className="h-11 bg-zinc-50 dark:bg-zinc-900/50 border-zinc-200 dark:border-zinc-800 focus:bg-white dark:focus:bg-zinc-900 focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all duration-200"
                                            />
                                        </div>

                                        <div className="grid gap-2 group">
                                            <Label htmlFor="profilePhone" className="text-xs text-zinc-500 font-medium group-focus-within:text-violet-600 transition-colors">Phone Number</Label>
                                            <div className="relative">
                                                <Phone className="absolute left-3.5 top-3.5 h-4 w-4 text-zinc-400 group-focus-within:text-violet-500 transition-colors" />
                                                <Input
                                                    id="profilePhone"
                                                    name="phone_number"
                                                    value={profileData.phone_number}
                                                    onChange={handleProfileChange}
                                                    className="pl-10 h-11 bg-zinc-50 dark:bg-zinc-900/50 border-zinc-200 dark:border-zinc-800 focus:bg-white dark:focus:bg-zinc-900 focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all duration-200"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pt-4">
                                        <Button
                                            onClick={handleProfileSubmit}
                                            disabled={profileMutation.isPending}
                                            className="w-full bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 h-12 rounded-xl shadow-lg shadow-zinc-200/50 dark:shadow-none hover:translate-y-[-1px] transition-all duration-300 font-semibold"
                                        >
                                            {profileMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                                            {profileMutation.isPending ? "Updating..." : "Update Profile"}
                                        </Button>
                                    </div>
                                </div>

                                {/* Read Only Info */}
                                <div className="space-y-4 pt-6">
                                    <div className="flex items-center gap-3 px-1">
                                        <Lock className="w-3.5 h-3.5 text-zinc-400" />
                                        <h4 className="font-medium text-xs text-zinc-500 uppercase tracking-wider">Security & System</h4>
                                    </div>

                                    <div className="grid gap-4 p-5 rounded-2xl bg-zinc-50/80 dark:bg-zinc-900/40 border border-zinc-100 dark:border-zinc-800/60 backdrop-blur-sm">
                                        <div className="flex flex-col gap-1.5">
                                            <Label className="text-[10px] text-zinc-400 uppercase font-bold tracking-wider">Email Address</Label>
                                            <div className="flex items-center gap-2.5 text-sm font-semibold text-zinc-700 dark:text-zinc-300">
                                                <div className="w-6 h-6 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                                                    <Mail className="w-3 h-3 text-zinc-500" />
                                                </div>
                                                {user?.email}
                                            </div>
                                        </div>
                                        <div className="h-px bg-zinc-100 dark:bg-zinc-800"></div>
                                        <div className="flex flex-col gap-1.5">
                                            <Label className="text-[10px] text-zinc-400 uppercase font-bold tracking-wider">Admin ID</Label>
                                            <div className="font-mono text-xs text-zinc-500 bg-zinc-100 dark:bg-zinc-800 px-2 py-1 rounded w-fit select-all">
                                                {user?._id}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </TabsContent>


                        {/* --- WAREHOUSE TAB --- */}
                        <TabsContent value="warehouse" className="m-0 p-0 focus-visible:outline-none animate-in fade-in slide-in-from-bottom-4 duration-500 ease-out">
                            <div className="p-6 space-y-8 pb-24">

                                {/* Refined Info Card */}
                                <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-100 dark:border-blue-800/30 rounded-2xl p-5 flex gap-4 transition-all duration-300 hover:shadow-md">
                                    <div className="bg-white dark:bg-blue-900/40 p-3 rounded-xl h-fit shadow-sm ring-1 ring-blue-100 dark:ring-blue-800/30 text-blue-600 dark:text-blue-400">
                                        <Briefcase className="w-6 h-6" />
                                    </div>
                                    <div className="flex-1 z-10">
                                        <h4 className="text-base font-bold text-blue-950 dark:text-blue-50">Distribution Hub</h4>
                                        <p className="text-xs text-blue-700/80 dark:text-blue-300/80 mt-1 leading-relaxed max-w-[90%]">
                                            Configure the primary operational center details for logistics and fulfillment.
                                        </p>
                                    </div>
                                    {/* Deco */}
                                    <div className="absolute top-0 right-0 -mt-6 -mr-6 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl"></div>
                                </div>

                                <div className="space-y-8">
                                    {/* Operational Details */}
                                    <div className="space-y-5 animate-in slide-in-from-bottom-2 duration-700 delay-100 fill-mode-backwards">
                                        <div className="flex items-center justify-between pb-2 border-b border-zinc-100 dark:border-zinc-800">
                                            <h4 className="text-sm font-semibold text-zinc-900 dark:text-white flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-violet-500"></div>
                                                General Operations
                                            </h4>
                                        </div>

                                        <div className="grid gap-5">
                                            <div className="grid gap-2 group">
                                                <Label htmlFor="whName" className="text-xs text-zinc-500 font-medium group-focus-within:text-blue-600 transition-colors">Warehouse Name</Label>
                                                <Input
                                                    id="whName"
                                                    name="name"
                                                    value={warehouseData.name}
                                                    onChange={handleWarehouseChange}
                                                    className="h-11 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                                                    placeholder="e.g. North Side Hub"
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="grid gap-2 group">
                                                    <Label htmlFor="whManager" className="text-xs text-zinc-500 font-medium group-focus-within:text-blue-600 transition-colors">Manager</Label>
                                                    <Input
                                                        id="whManager"
                                                        name="manager_name"
                                                        value={warehouseData.manager_name}
                                                        onChange={handleWarehouseChange}
                                                        className="h-11 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                                                    />
                                                </div>
                                                <div className="grid gap-2 group">
                                                    <Label htmlFor="whContact" className="text-xs text-zinc-500 font-medium group-focus-within:text-blue-600 transition-colors">Contact</Label>
                                                    <Input
                                                        id="whContact"
                                                        name="contact_number"
                                                        value={warehouseData.contact_number}
                                                        onChange={handleWarehouseChange}
                                                        className="h-11 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Location Details */}
                                    <div className="space-y-5 animate-in slide-in-from-bottom-2 duration-700 delay-200 fill-mode-backwards">
                                        <div className="flex items-center justify-between pb-2 border-b border-zinc-100 dark:border-zinc-800">
                                            <h4 className="text-sm font-semibold text-zinc-900 dark:text-white flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-pink-500"></div>
                                                Location
                                            </h4>
                                        </div>

                                        <div className="grid gap-5">
                                            <div className="grid gap-2 group">
                                                <Label htmlFor="whAddress" className="text-xs text-zinc-500 font-medium group-focus-within:text-pink-600 transition-colors">Street Address</Label>
                                                <Input
                                                    id="whAddress"
                                                    name="address"
                                                    value={warehouseData.address}
                                                    onChange={handleWarehouseChange}
                                                    className="h-11 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-all duration-200"
                                                />
                                            </div>
                                            <div className="grid grid-cols-6 gap-3">
                                                <div className="col-span-3 grid gap-2 group">
                                                    <Label htmlFor="whCity" className="text-xs text-zinc-500 font-medium group-focus-within:text-pink-600 transition-colors">City</Label>
                                                    <Input
                                                        id="whCity"
                                                        name="city"
                                                        value={warehouseData.city}
                                                        onChange={handleWarehouseChange}
                                                        className="h-11 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-all duration-200"
                                                    />
                                                </div>
                                                <div className="col-span-2 grid gap-2 group">
                                                    <Label htmlFor="whState" className="text-xs text-zinc-500 font-medium group-focus-within:text-pink-600 transition-colors">State</Label>
                                                    <Input
                                                        id="whState"
                                                        name="state"
                                                        value={warehouseData.state}
                                                        onChange={handleWarehouseChange}
                                                        className="h-11 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-all duration-200"
                                                    />
                                                </div>
                                                <div className="col-span-1 grid gap-2 group">
                                                    <Label htmlFor="whZip" className="text-xs text-zinc-500 font-medium group-focus-within:text-pink-600 transition-colors">Zip</Label>
                                                    <Input
                                                        id="whZip"
                                                        name="zipCode"
                                                        value={warehouseData.zipCode}
                                                        onChange={handleWarehouseChange}
                                                        className="h-11 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-all duration-200 px-1 text-center"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pt-6">
                                        <Button
                                            onClick={handleWarehouseSubmit}
                                            disabled={warehouseMutation.isPending}
                                            className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20 h-12 rounded-xl transition-all duration-300 hover:translate-y-[-1px] font-semibold"
                                        >
                                            {warehouseMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                            Save Warehouse Details
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </TabsContent>
                    </div>
                </Tabs>
            </SheetContent>
        </Sheet>
    );
};

export default AdminSettingsPanel;
